<template>
  <div v-if="visible" class="fixed inset-0 z-50 bg-white flex flex-col">
    <!-- 模态框内容 - 真正全屏 -->
    <div class="flex-1 flex flex-col h-full overflow-hidden p-6">
      <!-- 标题 -->
      <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 flex-shrink-0">
        <div>
          <h3 class="text-xl font-semibold text-gray-900">
            联系表单统计
          </h3>
          <div v-if="itemInfo" class="mt-2 flex items-center gap-4 flex-wrap">
            <p class="text-sm text-gray-700">
              <span class="font-medium">站点名称：</span>{{ itemInfo.name || itemInfo.site_name || '-' }}
            </p>
            <p class="text-xs text-gray-500">
              <span class="font-medium">站点ID：</span>{{ itemInfo.id }}
            </p>
            <p class="text-xs text-gray-500">
              <span class="font-medium">UUID：</span>{{ itemInfo.uid || itemInfo.uuid || '-' }}
            </p>
          </div>
        </div>
        <button
          @click="close"
          class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all"
          title="关闭"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <!-- 主体内容 -->
      <div class="flex-1 flex flex-col min-h-0">
            <div v-if="loading" class="flex items-center justify-center py-12">
              <div class="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
              <span class="ml-3 text-gray-600">加载中...</span>
            </div>
            
            <!-- 统计摘要 -->
            <div v-else-if="contacts.length > 0" class="space-y-4 flex-1 flex flex-col min-h-0">
              <div class="grid grid-cols-3 gap-3 flex-shrink-0">
                <!-- 总提交数 -->
                <div class="bg-blue-50 rounded-lg p-3 text-center">
                  <div class="text-lg font-semibold text-blue-600">{{ total }}</div>
                  <div class="text-xs text-blue-700">总提交数</div>
                </div>
                
                <!-- 手机端提交 -->
                <div class="bg-green-50 rounded-lg p-3 text-center">
                  <div class="text-lg font-semibold text-green-600">{{ mobileCount }}</div>
                  <div class="text-xs text-green-700">手机端提交</div>
                </div>
                
                <!-- 电脑端提交 -->
                <div class="bg-purple-50 rounded-lg p-3 text-center">
                  <div class="text-lg font-semibold text-purple-600">{{ desktopCount }}</div>
                  <div class="text-xs text-purple-700">电脑端提交</div>
                </div>
              </div>
              
              <!-- 联系表单记录列表 -->
              <div class="bg-white border border-gray-200 rounded-lg overflow-hidden flex-1 flex flex-col min-h-0">
                <div class="overflow-y-auto flex-1">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">时间</th>
                        <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">姓名</th>
                        <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">邮箱</th>
                        <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP地址</th>
                        <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">位置</th>
                        <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">设备</th>
                        <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">留言内容</th>
                        <th scope="col" class="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="contact in contacts" :key="contact.id" class="hover:bg-gray-50">
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                          {{ formatDateTime(contact.submitted_at || contact.created_at) }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {{ contact.name }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                          <a :href="'mailto:' + contact.email" class="text-blue-600 hover:underline">
                            {{ contact.email }}
                          </a>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                          {{ contact.ip_address }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                          🌍 {{ contact.location_display }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                          <span v-if="contact.device_type === 'desktop'">💻 电脑</span>
                          <span v-else-if="contact.device_type === 'mobile'">📱 手机</span>
                          <span v-else-if="contact.device_type === 'tablet'">📱 平板</span>
                          <span v-else>-</span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-800">
                          <div class="max-w-xs truncate" :title="contact.message">
                            {{ contact.message }}
                          </div>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <button
                            @click="replyToContact(contact)"
                            class="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                            :title="'回复 ' + contact.email"
                          >
                            <svg class="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            回复
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <!-- 空状态 -->
            <div v-else class="flex flex-col items-center justify-center py-12 text-gray-500 flex-1">
              <svg class="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p>暂无联系表单提交</p>
            </div>
      </div>
      
      <!-- 分页控件 -->
      <div v-if="totalPages > 1" class="mt-4 flex items-center justify-center space-x-2 flex-shrink-0">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          上一页
        </button>
        
        <div class="flex items-center space-x-1">
          <!-- 第一页 -->
          <button
            v-if="currentPage > 3"
            @click="goToPage(1)"
            class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            1
          </button>
          <span v-if="currentPage > 4" class="px-2 text-gray-500">...</span>
          
          <!-- 当前页附近的页码 -->
          <button
            v-for="page in getPageNumbers()"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              page === currentPage
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ page }}
          </button>
          
          <!-- 最后一页 -->
          <span v-if="currentPage < totalPages - 3" class="px-2 text-gray-500">...</span>
          <button
            v-if="currentPage < totalPages - 2"
            @click="goToPage(totalPages)"
            class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {{ totalPages }}
          </button>
        </div>
        
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          下一页
        </button>
        
        <span class="ml-2 text-sm text-gray-600">
          第 {{ currentPage }} / {{ totalPages }} 页
        </span>
      </div>
    </div>
  </div>
  
  <!-- 邮件回复模态框 -->
  <EmailReplyModal
    :show-modal="showReplyModal"
    :is-reply="false"
    :target-customer="selectedContact"
    @close="closeReplyModal"
    @sent="handleEmailSent"
  />
</template>

<script setup>
import { ref, watch } from 'vue'
import EmailReplyModal from '@/components/EmailReplyModal.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  itemId: {
    type: [String, Number],
    default: null
  },
  itemInfo: {
    type: Object,
    default: null
  },
  apiFunction: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['update:visible', 'close'])

const loading = ref(false)
const contacts = ref([])
const total = ref(0)
const mobileCount = ref(0)
const desktopCount = ref(0)
const currentPage = ref(1)
const pageSize = ref(20)
const totalPages = ref(0)

// 邮件回复相关状态
const showReplyModal = ref(false)
const selectedContact = ref(null)

watch(() => props.visible, async (newVal) => {
  if (newVal && props.itemId) {
    currentPage.value = 1
    await fetchContacts()
  }
})

const fetchContacts = async () => {
  loading.value = true
  try {
    const response = await props.apiFunction(props.itemId, currentPage.value, pageSize.value)
    if (response.success) {
      contacts.value = response.data || []
      total.value = response.total || 0
      mobileCount.value = response.mobile_count || 0
      desktopCount.value = response.desktop_count || 0
      totalPages.value = response.total_pages || 0
    }
  } catch (error) {
    console.error('获取联系表单记录失败:', error)
  } finally {
    loading.value = false
  }
}

const goToPage = async (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return
  currentPage.value = page
  await fetchContacts()
}

const getPageNumbers = () => {
  const pages = []
  const maxVisible = 5 // 最多显示5个页码
  
  let start = Math.max(1, currentPage.value - 2)
  let end = Math.min(totalPages.value, start + maxVisible - 1)
  
  // 如果末尾不够，调整起始位置
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
}

const close = () => {
  emit('update:visible', false)
  emit('close')
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// 回复联系表单
const replyToContact = (contact) => {
  // 将联系表单信息转换为客户对象格式，用于 EmailReplyModal
  // 🔧 智能判断表单类型：通过检查数据字段来区分品牌独立站联系表单和二维码表单提交
  const isQRCodeSubmission = contact.hasOwnProperty('feedback') || contact.hasOwnProperty('order_number')
  
  selectedContact.value = {
    id: null, // 联系表单没有客户ID
    email: contact.email,
    name: contact.name,
    first_name: contact.name.split(' ')[0] || contact.name,
    last_name: contact.name.split(' ').slice(1).join(' ') || ''
  }
  
  // 根据表单类型携带不同的ID
  if (isQRCodeSubmission) {
    selectedContact.value.submission_id = contact.id  // 🔧 二维码表单提交ID
  } else {
    selectedContact.value.contact_id = contact.id  // 🔧 品牌独立站联系表单ID
  }
  
  showReplyModal.value = true
}

// 关闭邮件回复模态框
const closeReplyModal = () => {
  showReplyModal.value = false
  selectedContact.value = null
}

// 邮件发送成功处理
const handleEmailSent = () => {
  // 邮件发送成功后，可以重新加载列表或者不做处理
  console.log('邮件发送成功')
}
</script>

<style scoped>
/* 自定义样式 */
</style>

